import { apiJson, ApiError } from '@/api/client'
import type { CaseRichSummary } from '@/features/alerts/types'
import type {
  AddIpToListPayload,
  AddIpToListResponse,
  AmlHandoffPayload,
  AmlHandoffResponse,
  ApproveUnbindPayload,
  CaseDetail,
  CaseNote,
  CaseSummary,
  FlagAgentPayload,
  FlagAgentResponse,
  GroupingSuggestion,
  PushCustomerPayload,
  PushCustomerResponse,
  StepUpOtpPayload,
  StepUpOtpResponse,
  SuggestionReason,
  SuggestionReasonCode,
  TerminateSessionsPayload,
  TerminateSessionsResponse,
  UnbindDevicePayload,
  UnbindDeviceResponse,
  UnlinkAlertsPayload,
} from './types'

export function getCase(id: string): Promise<CaseDetail> {
  return apiJson<CaseDetail>(`/cases/${id}`)
}

// ---- disposition actions -------------------------------------------
//
// All three POST to /cases/{id}/actions/{approve|decline|escalate}
// and require a non-empty reason (≥10 chars enforced server-side).
// Backend resolves the case + flips the linked transaction's status
// + writes the case_action audit row in one tx.

export interface CaseDispositionPayload {
  reason: string
  evidence_ids?: string[]
}

export interface CaseDispositionResult {
  /** New case status. */
  status: CaseDetail['status']
  /** Final resolution (false_positive / fraud_confirmed). */
  resolution?: 'false_positive' | 'fraud_confirmed'
  /** Closed-at / resolved-at timestamps echoed back. */
  resolved_at?: string
}

export function approveCase(id: string, payload: CaseDispositionPayload) {
  return apiJson<CaseDispositionResult>(`/cases/${id}/actions/approve`, {
    method: 'POST',
    body: payload,
  })
}

export function declineCase(id: string, payload: CaseDispositionPayload) {
  return apiJson<CaseDispositionResult>(`/cases/${id}/actions/decline`, {
    method: 'POST',
    body: payload,
  })
}

export function escalateCase(id: string, payload: CaseDispositionPayload) {
  return apiJson<CaseDispositionResult>(`/cases/${id}/actions/escalate`, {
    method: 'POST',
    body: payload,
  })
}

/**
 * BR-014 — refer a branch-owned case to HQ. Doesn't change branch
 * ownership; just flags the case so HQ analysts see it in their
 * workspace. Reason is required (free text); the audit row records
 * who referred and why.
 */
export interface ReferToHQResult {
  case_id: string
  escalated_to_hq_at: string
  reason: string
}
export function referCaseToHQ(id: string, reason: string) {
  return apiJson<ReferToHQResult>(`/cases/${id}/actions/refer-to-hq`, {
    method: 'POST',
    body: { reason },
  })
}

// ChangeStatus is the broader workflow transition — used by the
// "Reopen" affordance (closed → in_review) and any custom status
// flips outside the canonical approve/decline/escalate set.
export interface CaseStatusChangePayload {
  status: CaseDetail['status']
  reason?: string
  notes?: string
}

export function changeCaseStatus(id: string, payload: CaseStatusChangePayload) {
  return apiJson<CaseDetail>(`/cases/${id}/status`, {
    method: 'POST',
    body: payload,
  })
}

// ---- case notes ---------------------------------------------------
//
// Backend exposes GET /cases/{id}/notes (cases:read) and POST
// /cases/{id}/notes (cases:write). The wire shape for both is
// CaseNote — see types.ts. POST returns 201 with the new row;
// GET returns `{ items: CaseNote[] }`.

export interface AddCaseNotePayload {
  /** Free-text body. Backend rejects empty / whitespace-only. */
  content: string
  /** Defaults to "comment" server-side; override for decision /
   *  next_step / blocker / customer_contact entries. */
  note_type?: string
}

export async function listCaseNotes(id: string): Promise<CaseNote[]> {
  const res = await apiJson<{ items: CaseNote[] }>(`/cases/${id}/notes`)
  return res.items ?? []
}

export function addCaseNote(id: string, payload: AddCaseNotePayload) {
  return apiJson<CaseNote>(`/cases/${id}/notes`, {
    method: 'POST',
    body: payload,
  })
}

export interface ListCasesArgs {
  q?: string
  page?: number
  pageSize?: number
  /** Repeatable in the wire query string. */
  status?: Array<CaseRichSummary['status']>
  priority?: Array<CaseRichSummary['priority']>
  /** User public_id, or the literal "unassigned". */
  assigned_to?: string[]
  created_from?: string
  created_to?: string
  /** "created_at" | "priority" | "sla_deadline". */
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
}

export interface CasesListResponse {
  cases: CaseRichSummary[]
  total: number
}

export async function listCases(args: ListCasesArgs = {}): Promise<CasesListResponse> {
  // Go envelopes paginated lists as `{items, total}`. React side
  // expects `{cases: [...], total}`. Translate at the adapter —
  // same posture as listAlerts.
  const sp = new URLSearchParams()
  if (args.q) sp.set('q', args.q)
  sp.set('page', String(args.page ?? 1))
  sp.set('page_size', String(args.pageSize ?? 50))
  for (const s of args.status ?? []) sp.append('status', s)
  for (const p of args.priority ?? []) sp.append('priority', p)
  for (const a of args.assigned_to ?? []) sp.append('assigned_to', a)
  if (args.created_from) sp.set('created_from', args.created_from)
  if (args.created_to) sp.set('created_to', args.created_to)
  if (args.sort_by) sp.set('sort_by', args.sort_by)
  if (args.sort_dir) sp.set('sort_dir', args.sort_dir)
  const wire = await apiJson<{
    cases?: CaseRichSummary[]
    items?: CaseRichSummary[]
    total?: number
  }>(`/cases?${sp.toString()}`)
  return {
    cases: wire.cases ?? wire.items ?? [],
    total: wire.total ?? 0,
  }
}

/**
 * AF-127 — Unlink alerts from a case.
 *
 * The Go cases-handler exposes `POST /cases/{id}/unlink` with a
 * body of `{alert_ids, reason}` and a response envelope of
 * `{unlinked: N, failed: [{alert_id, code, message}]}`. The
 * operator-supplied reason flows into each per-alert audit row.
 *
 * Partial failure is data, not HTTP-level error: the server always
 * returns 200 with the envelope. We re-throw as `ApiError` when
 * any rows failed so the existing toast path in `useUnlinkAlerts`
 * keeps treating it the same way.
 *
 * (Earlier this adapter fanned out per-id DELETE calls because the
 * server lacked a body-bearing bulk endpoint. The bulk endpoint
 * shipped — fan-out is no longer needed.)
 */
interface BulkUnlinkResponse {
  unlinked: number
  failed: Array<{ alert_id: string; code: string; message: string }>
}

export async function unlinkAlerts(
  payload: UnlinkAlertsPayload,
): Promise<{ unlinked: number }> {
  const { case_id: caseId, alert_ids: alertIds, reason } = payload
  if (alertIds.length === 0) return { unlinked: 0 }

  const res = await apiJson<BulkUnlinkResponse>(
    `/cases/${caseId}/unlink`,
    {
      method: 'POST',
      body: { alert_ids: alertIds, reason },
    },
  )

  if (res.failed.length > 0) {
    // Re-shape into the ApiError contract so the toast path in
    // useUnlinkAlerts gets the same shape it did under the old
    // fan-out adapter. The first failure's code is the
    // representative wire-error code; details carries the full
    // per-id failure list for callers who want to highlight
    // individual rows.
    const first = res.failed[0]!
    throw new ApiError(
      `Unlinked ${res.unlinked} of ${alertIds.length} alert${alertIds.length === 1 ? '' : 's'}; ${res.failed.length} failed`,
      207,
      first.code || 'unlink_partial_failure',
      { failed: res.failed },
    )
  }
  return { unlinked: res.unlinked }
}

/**
 * AF-127 — Suggested groupings.
 *
 * Go's `/alerts/{id}/suggested-groupings` (see
 * `internal/api/alerts_handler.go::SuggestedGroupings` and
 * `internal/cases/grouping/service.go`) returns a flat list of
 * related alerts under `items`, where each row carries:
 *
 *   { alert_id, alert_number, score, priority, status, summary,
 *     created_at, reasons: string[] }
 *
 * The React UI consumes a *clustered* shape — one suggestion per
 * (set of reasons) with the related alert ids bundled, plus an
 * optional `candidate_case`. The adapter clusters by reason set
 * client-side so the panel shows "+N alerts match · {reason}"
 * groups instead of N singleton rows.
 *
 * Optional wire extensions (`case_id`/`case_number`/...,
 * `reason_details`) are forward-compatibility hooks the MSW mocks
 * exercise so tests can assert the rich UI behaviour. The Go
 * handler doesn't emit them today — the adapter degrades to plain
 * label-only reasons + null `candidate_case` when they're absent.
 */
interface GoSuggestedGroupingItem {
  alert_id: string
  alert_number: string
  score: number
  priority: string
  status: string
  summary: string
  created_at: string
  reasons: string[]
  // Forward-compatibility extensions (mock-only today).
  case_id?: string
  case_number?: string
  case_title?: string
  case_status?: CaseSummary['status']
  case_alert_count?: number
  reason_details?: Array<{
    code: string
    label?: string
    detail?: string
  }>
}

interface GoSuggestedGroupingsResponse {
  items: GoSuggestedGroupingItem[]
}

const REASON_DEFAULT_LABELS: Record<string, string> = {
  same_customer_24h: 'Same customer · last 24h',
  entity_overlap: 'Shared entity overlap',
  same_scenario_1h: 'Same scenario · last 1h',
  shared_device: 'Shared device fingerprint',
  shared_ip: 'Shared IP / fingerprint',
  shared_counterparty_nuban: 'Shared counterparty NUBAN',
  shared_msisdn: 'Shared MSISDN',
  related_session: 'Related session',
}

const MAX_GROUPING_SUGGESTIONS = 5

export async function listGroupingSuggestions(
  alertId: string,
): Promise<{ suggestions: GroupingSuggestion[] }> {
  const res = await apiJson<GoSuggestedGroupingsResponse>(
    `/alerts/${alertId}/suggested-groupings`,
  )

  // Bucket by sorted reason-set so rows that match for the same
  // reasons cluster into one suggestion. Stable sort key keeps the
  // output deterministic across renders.
  const buckets = new Map<string, GoSuggestedGroupingItem[]>()
  for (const row of res.items) {
    const key = [...row.reasons].sort().join('|')
    const bucket = buckets.get(key)
    if (bucket) bucket.push(row)
    else buckets.set(key, [row])
  }

  const suggestions: GroupingSuggestion[] = Array.from(
    buckets.entries(),
  ).map(([key, rows], i) => {
    const first = rows[0]!
    const reasons: SuggestionReason[] = first.reason_details
      ? first.reason_details.map((rd) => ({
          code: rd.code as SuggestionReasonCode,
          label: rd.label ?? REASON_DEFAULT_LABELS[rd.code] ?? rd.code,
          ...(rd.detail !== undefined ? { detail: rd.detail } : {}),
        }))
      : first.reasons.map((code) => ({
          code: code as SuggestionReasonCode,
          label: REASON_DEFAULT_LABELS[code] ?? code,
        }))

    // Confidence proxy: mean of the per-alert scores in the cluster.
    // Go's grouping service doesn't emit a cluster confidence today;
    // mean-score is a defensible stand-in until it does.
    const confidence = Math.round(
      rows.reduce((acc, r) => acc + r.score, 0) / rows.length,
    )

    // Pick the first row in the cluster that carries a case linkage —
    // any of the rows would fold into the same case under "Add to
    // <case>" semantics. Null in production today (Go-missing).
    const linked = rows.find((r) => r.case_id !== undefined)
    const candidate_case: CaseSummary | null =
      linked && linked.case_id && linked.case_number
        ? {
            id: linked.case_id,
            case_number: linked.case_number,
            title: linked.case_title ?? '',
            status: linked.case_status ?? 'open',
            alert_count: linked.case_alert_count ?? 0,
          }
        : null

    return {
      id: `sug-${alertId}-${key.replace(/[^a-z0-9]/gi, '-') || 'untagged'}-${i}`,
      alert_ids: rows.map((r) => r.alert_id),
      reasons,
      confidence,
      candidate_case,
    }
  })

  // Top-N by confidence, per the AC ("top-5 grouping suggestions").
  suggestions.sort((a, b) => b.confidence - a.confidence)
  return { suggestions: suggestions.slice(0, MAX_GROUPING_SUGGESTIONS) }
}

/** AF-244 — step-up OTP to a known-good alternate MSISDN. */
export function triggerStepUpOtp(
  payload: StepUpOtpPayload,
): Promise<StepUpOtpResponse> {
  return apiJson<StepUpOtpResponse>(`/cases/${payload.case_id}/step-up-otp`, {
    method: 'POST',
    body: {
      alternate_msisdn: payload.alternate_msisdn,
      reason: payload.reason,
    },
  })
}

/** AF-245 — request to unbind an active device (maker side). */
export function requestUnbindDevice(
  payload: UnbindDevicePayload,
): Promise<UnbindDeviceResponse> {
  return apiJson<UnbindDeviceResponse>(
    `/cases/${payload.case_id}/unbind-device`,
    {
      method: 'POST',
      body: { device_id: payload.device_id, reason: payload.reason },
    },
  )
}

/** AF-245 — second analyst approves the pending unbind. */
export function approveUnbindDevice(
  payload: ApproveUnbindPayload,
): Promise<{ approved: true }> {
  return apiJson<{ approved: true }>(
    `/cases/${payload.case_id}/unbind-device/${payload.request_id}/approve`,
    { method: 'POST' },
  )
}

/** AF-245 — push case context to all customer devices (with sms+email fallback). */
export function pushCaseNotification(
  payload: PushCustomerPayload,
): Promise<PushCustomerResponse> {
  return apiJson<PushCustomerResponse>(
    `/cases/${payload.case_id}/push-customer`,
    {
      method: 'POST',
      body: { channels: payload.channels, message: payload.message },
    },
  )
}

/** AF-246 — terminate every active web session for the customer. */
export function terminateSessions(
  payload: TerminateSessionsPayload,
): Promise<TerminateSessionsResponse> {
  return apiJson<TerminateSessionsResponse>(
    `/cases/${payload.case_id}/terminate-sessions`,
    {
      method: 'POST',
      body: { reason: payload.reason, force_reauth: payload.force_reauth },
    },
  )
}

/** AF-246 — add the session IP to the watchlist or blocklist (maker side). */
export function addIpToList(
  payload: AddIpToListPayload,
): Promise<AddIpToListResponse> {
  return apiJson<AddIpToListResponse>(`/cases/${payload.case_id}/ip-list`, {
    method: 'POST',
    body: {
      ip_address: payload.ip_address,
      list: payload.list,
      reason: payload.reason,
    },
  })
}

/** AF-247 — package the case for the AML team queue. */
export function amlHandoff(
  payload: AmlHandoffPayload,
): Promise<AmlHandoffResponse> {
  return apiJson<AmlHandoffResponse>(`/cases/${payload.case_id}/aml-handoff`, {
    method: 'POST',
    body: { summary: payload.summary, notes: payload.notes },
  })
}

/** AF-247 — flag the agent for review (notifies super-agent + AML team). */
export function flagAgent(
  payload: FlagAgentPayload,
): Promise<FlagAgentResponse> {
  return apiJson<FlagAgentResponse>(`/cases/${payload.case_id}/flag-agent`, {
    method: 'POST',
    body: {
      agent_id: payload.agent_id,
      reason: payload.reason,
      notify_super_agent: payload.notify_super_agent,
    },
  })
}

/**
 * KUP-API-008 — Recent alerts that are not attached to any case.
 *
 * The "no case" predicate is evaluated SERVER-SIDE via `unlinked=true`
 * (case_id IS NULL) combined with the open-ish status set. Never filter a
 * fetched page client-side: that is the anti-pattern that hides rows
 * beyond the page boundary. Sort is left to the server default
 * (created_at desc) so this does not depend on the `sort` param shape.
 */
export const UNLINKED_ALERT_STATUSES = ['open', 'in_review', 'escalated'] as const

export interface UnlinkedAlertRow {
  id: string
  alert_number: string
  scenario_name?: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  score: number
  channel?: string | null
  customer?: { display_name?: string; customer_number?: string } | null
  created_at: string
}

export interface UnlinkedAlertsResponse {
  alerts: UnlinkedAlertRow[]
  total: number
}

export async function listUnlinkedAlerts(
  pageSize = 10,
): Promise<UnlinkedAlertsResponse> {
  const sp = new URLSearchParams()
  sp.set('unlinked', 'true')
  for (const s of UNLINKED_ALERT_STATUSES) sp.append('status', s)
  sp.set('page', '1')
  sp.set('page_size', String(pageSize))
  const wire = await apiJson<{
    alerts?: UnlinkedAlertRow[]
    items?: UnlinkedAlertRow[]
    total?: number
  }>(`/alerts?${sp.toString()}`)
  const alerts = wire.alerts ?? wire.items ?? []
  return { alerts, total: wire.total ?? alerts.length }
}
